/**
 * Engagement Settings — dialog for editing a live engagement's settings.
 *
 * Exposes:
 *   • Engagement type (Batch 0 Phase 2 — Partner/Admin only; hide-not-delete).
 *   • Presentation Framework (R13 — editable until first FS generation).
 *   • Requires EQC review (ISA 220 R33) — Partner/Admin only. Toggling ON
 *     is always allowed; toggling OFF is blocked server-side once ISQM 2
 *     forms on this engagement already carry data.
 */
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { clientName } from "@/lib/engagementLabel";   // Batch 8.4 · A3 (F-54)
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DateInput from "@/components/DateInput";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ShieldCheck, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import { confirmDiscard } from "@/lib/unsavedChanges";
import EngagementLocaleFields, { localeValueOf, localePayload, currencyOfValue } from "@/components/EngagementLocaleFields";   // PR 45 review · D2
import { suggestedWeekend, sameDays, otherCurrencyError, OTHER, countryName } from "@/lib/engagementLocale";
import FirstYearToggle from "@/components/FirstYearToggle";   // PR 58 / 59 fixes · F2
import { errText } from "@/lib/i18n/1250_b34g";   // SUNDAY BATCH · FX2-5: a refusal in the screen's language (the server's detail_ar)

// Batch 0 Part 1 — mirrors engagement_types.py. Round 24c · M2: the four
// coming-soon types are HIDDEN (flag only — code/data/forms kept) until built.
const TYPE_OPTIONS = [
  { id: "audit",             selectable: true  },
  { id: "review",            selectable: true  },
  { id: "interim_review",    selectable: false, hidden: true },
  { id: "special_purpose",   selectable: false, hidden: true },
  { id: "single_statement",  selectable: false, hidden: true },
  { id: "summary_fs",        selectable: false, hidden: true },
].filter((o) => !o.hidden);

export default function EngagementSettingsDialog({ engagement, open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [pf, setPf] = useState("");
  const [requiresEqc, setRequiresEqc] = useState(false);
  const [engType, setEngType] = useState("audit");
  const [engStatus, setEngStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [repFw, setRepFw] = useState("full");
  // Batch 2 · R65 — engagement period (start defaults to 12 months before the end; editable here)
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  // Round 25c · R5/R7 — report flags + report format (saved via /report routes; logged; 423 on locked).
  const [flags, setFlags] = useState({});
  const [formats, setFormats] = useState([]);
  const [rptFmt, setRptFmt] = useState("platform");
  // PR 45 review · D2 (Card V3): country, currency and weekend — edited here (round 2 · R2: any team member, Admin, Partner)
  const [locale, setLocale] = useState(() => localeValueOf(engagement));
  const [localeErr, setLocaleErr] = useState({});
  // PR 45 round 2 · R9: an older engagement's currency is "to confirm" until a team member saves it once
  const [confirmCurrency, setConfirmCurrency] = useState(false);
  useEffect(() => {
    if (!open || !engagement) return;
    setFlags(engagement.report_flags || {});
    setRptFmt(engagement.report_format?.kind === "firm" ? engagement.report_format.format_id : "platform");
    if (user?.role === "Partner") api.get("/report/formats", { _silent: true }).then((r) => setFormats(asArray(r.data).filter((f) => f.status === "approved"))).catch(() => {});
  }, [open, engagement, user?.role]);
  // Batch 28 · Follow-up 1 · F2: the report flags and the report format are part of the dialog's one Save / Cancel bar (they
  // used to save on their own button / on every change); Save writes each changed part once through its existing route.
  const savedFmt = engagement?.report_format?.kind === "firm" ? engagement.report_format.format_id : "platform";
  const canToggleEqc = user?.role === "Partner" || user?.role === "Admin";
  const canChangeType = user?.role === "Partner" || user?.role === "Admin";

  const restore = () => {
    setPf(engagement?.presentation_framework || "");
    setRequiresEqc(!!engagement?.requires_eqc);
    setEngType(engagement?.engagement_type || "");
    setEngStatus(engagement?.initial_or_continuing || "");
    setRepFw(engagement?.reporting_framework || "full");
    setPeriodStart(engagement?.period_start || "");
    setPeriodEnd(engagement?.period_end || "");
    setFlags(engagement?.report_flags || {});
    setRptFmt(savedFmt);
    setLocale(localeValueOf(engagement)); setLocaleErr({}); setConfirmCurrency(false);
  };

  const resetFromEngagement = useEffectEvent(() => {
    setPf(engagement?.presentation_framework || "");
    setRequiresEqc(!!engagement?.requires_eqc);
    // Q3 — don't fabricate "audit" when the stored type is empty.
    // Highlight nothing so the auditor sees "Not set" until they
    // actively pick a type.
    setEngType(engagement?.engagement_type || "");
    setEngStatus(engagement?.initial_or_continuing || "");
    setRepFw(engagement?.reporting_framework || "full");
    setPeriodStart(engagement?.period_start || "");
    setPeriodEnd(engagement?.period_end || "");
    setLocale(localeValueOf(engagement)); setLocaleErr({}); setConfirmCurrency(false);
  });
  useEffect(() => {
    if (open) resetFromEngagement();
  }, [open, engagement?.id, engagement?.presentation_framework, engagement?.requires_eqc, engagement?.engagement_type, engagement?.initial_or_continuing, engagement?.reporting_framework, resetFromEngagement]);

  if (!engagement) return null;
  const fsGenerated = !!engagement.fs_first_generated_at;
  const engLocked = !!engagement.signed_off;
  // Round 24c · M1 — frameworks may be changed after FS generation (warning + log, nothing deleted).
  const fwMismatch = !!pf && (repFw || "full") === "sme";

  // PR 45 review · D2: what changed in the country / currency / weekend section
  const savedLocale = localeValueOf(engagement);
  const countryDirty = (locale.country || "") !== (savedLocale.country || "");
  const currencyDirty = currencyOfValue(locale) !== currencyOfValue(savedLocale) || (locale.currencyChoice === OTHER) !== (savedLocale.currencyChoice === OTHER);
  const weekendDirty = !!locale.weekendTouched && (!sameDays(locale.no_weekend ? [] : locale.weekend_days, savedLocale.weekend_days)
                                                   || !Array.isArray(engagement.non_working_days?.weekdays));
  const currencyToConfirm = !engagement.currency_confirmed_at && !!currencyOfValue(savedLocale);   // R9
  const confirmDirty = currencyToConfirm && confirmCurrency && !currencyDirty;
  const localeDirty = countryDirty || currencyDirty || weekendDirty || confirmDirty;
  const suggested = suggestedWeekend(locale.country);
  const weekendNow = locale.no_weekend ? [] : (locale.weekend_days || []);
  const weekendDiffers = !!suggested && (locale.weekendTouched || Array.isArray(engagement.non_working_days?.weekdays)) && !sameDays(weekendNow, suggested);
  const dayList = (ds) => (ds.length ? ds.map((d) => t(`engLocale.days.${d}`)).join(", ") : t("engLocale.noWeekend"));
  const flagsDirty = JSON.stringify(flags || {}) !== JSON.stringify(engagement.report_flags || {});
  const fmtDirty = user?.role === "Partner" && rptFmt !== savedFmt;
  const settingsDirty =
    (pf || "") !== (engagement.presentation_framework || "") ||
    (repFw || "full") !== (engagement.reporting_framework || "full") ||
    !!requiresEqc !== !!engagement.requires_eqc ||
    (engType || "") !== (engagement.engagement_type || "") ||
    (engStatus || "") !== (engagement.initial_or_continuing || "") ||
    (periodStart || "") !== (engagement.period_start || "") ||
    (periodEnd || "") !== (engagement.period_end || "");
  const dirty = settingsDirty || flagsDirty || fmtDirty || localeDirty;
  const close = async () => { if (await confirmDiscard(dirty, lang)) onOpenChange(false); };

  const save = async () => {
    if (!pf) {
      toast.error(t("engSettings.pickPf"));
      return;
    }
    // PR 45 review · D2: a changed field must be complete (the server refuses too)
    const lerr = {};
    if (countryDirty && !locale.country) lerr.country = "countryRequired";
    if (currencyDirty) {
      if (!locale.currencyChoice) lerr.currency = "currencyRequired";
      else if (locale.currencyChoice === OTHER && otherCurrencyError(locale.currencyOther)) lerr.currency = otherCurrencyError(locale.currencyOther) === "required" ? "otherRequired" : "otherInvalid";
    }
    if (weekendDirty && !weekendNow.length && !locale.no_weekend) lerr.weekend = "weekendRequired";
    setLocaleErr(lerr);
    if (Object.keys(lerr).length) { toast.error(t(`engLocale.${Object.values(lerr)[0]}`)); return; }
    setSaving(true);
    try {
      const payload = {};
      const lp = localePayload(locale);
      if (countryDirty) payload.country = lp.country;
      if (currencyDirty) {
        const from = currencyOfValue(savedLocale) || "—";
        if (!await askConfirm(t("engLocale.relabelConfirm").replace("{from}", from).replace("{to}", lp.currency))) { setSaving(false); return; }
        payload.currency = lp.currency;
      } else if (confirmDirty) {
        payload.currency = currencyOfValue(savedLocale);   // R9: the same value, saved once = confirmed (Audit Trail row)
      }
      if (weekendDirty && !await askConfirm(t("engLocale.weekendStaleConfirm"))) { setSaving(false); return; }
      if ((pf || "") !== (engagement.presentation_framework || "")) {
        if (fsGenerated && !await askConfirm(t("engSettings.pfWarn").replace("{from}", engagement.presentation_framework || "—").replace(/\{to\}/g, pf))) { setSaving(false); return; }
        payload.presentation_framework = pf;
      }
      if (canToggleEqc && !!requiresEqc !== !!engagement.requires_eqc) {
        payload.requires_eqc = !!requiresEqc;
      }
      if (canChangeType && (engType || "") !== (engagement.engagement_type || "")) {
        payload.engagement_type = engType;
      }
      if ((engStatus || "") !== (engagement.initial_or_continuing || "")) {
        payload.initial_or_continuing = engStatus;
      }
      if ((periodStart || "") !== (engagement.period_start || "")) payload.period_start = periodStart;
      if ((periodEnd || "") !== (engagement.period_end || "")) payload.period_end = periodEnd;
      if ((repFw || "full") !== (engagement.reporting_framework || "full")) {
        const cur = engagement.reporting_framework || "full";
        try {
          const r = await api.get(`/disclosure-checklist/${engagement.id}/framework`, { _silent: true });
          const n = r.data?.answers?.[cur] || 0;
          if (n > 0 && !await askConfirm(t("engSettings.rfWarn").replace("{n}", String(n)))) { setSaving(false); return; }
        } catch { /* no warning data → proceed */ }
        payload.reporting_framework = repFw;
      }
      if (Object.keys(payload).length === 0 && !flagsDirty && !fmtDirty && !weekendDirty) {
        onOpenChange(false);
        return;
      }
      if (Object.keys(payload).length) {
        await api.patch(`/engagements/${engagement.id}`, payload);
        toast.success(t("engSettings.saved"));
      }
      // PR 45 review · D2: the weekend goes through the engagement-settings route (Audit Trail row old → new, readers marked re-run)
      if (weekendDirty) {
        await api.patch(`/engagements/${engagement.id}/settings`, { weekend_weekdays: lp.weekend_days });
        if (!Object.keys(payload).length) toast.success(t("engSettings.saved"));
      }
      if (flagsDirty) { await api.put(`/report/${engagement.id}/flags`, { flags }); toast.success(t("engSettings.flagsSaved")); }
      if (fmtDirty) {
        await api.put(`/report/${engagement.id}/report-format`, rptFmt === "platform" ? { kind: "platform" } : { kind: "firm", format_id: rptFmt });
        toast.success(t("engSettings.formatSaved"));
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e, lang, toErrMsg(e, t("engSettings.saveFailed"))));   // FX2-5: AR shows the server's detail_ar
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (v) onOpenChange(v); else close(); }}>
      <DialogContent data-testid="engagement-settings-dialog"
                      className="flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{t("engSettings.title")} — {clientName(engagement, t)}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
              data-testid="eng-settings-body">
          {/* Engagement type — Partner/Admin only. Coming-soon types stay
              disabled. Hide-not-delete semantics: every change flips
              section visibility, no rows are dropped from the database. */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40">
            <Label className="flex items-center gap-1.5 text-xs font-semibold">
              <Briefcase className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" />
              {t("engSettings.engType")}
            </Label>
            <div className="mt-2 grid grid-cols-1 gap-1.5" role="radiogroup"
                  aria-label={t("engSettings.engType")}
                  data-testid="eng-settings-type-group">
              {TYPE_OPTIONS.map((opt) => {
                const active = engType === opt.id;
                const disabled = !canChangeType || !opt.selectable || engLocked;
                return (
                  <button key={opt.id} type="button" role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    onClick={() => opt.selectable && setEngType(opt.id)}
                    className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition ${
                      active
                        ? "border-emerald-500 bg-white font-semibold text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300"
                        : "border-slate-200 bg-transparent text-slate-700 hover:bg-white dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/60"
                    } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    data-testid={`eng-settings-type-${opt.id}`}>
                    <span>{t(`engagementType.${opt.id}`)}</span>
                    {!opt.selectable && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        {t("engagementType.comingSoonBadge")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] leading-snug text-muted-foreground">
              {canChangeType
                ? t("engSettings.engTypeHideNotDelete")
                : t("engSettings.engTypePartnerOnly")}
            </div>
          </div>

          {/* PR 45 review · D2 (Card V3) — country, currency (relabel only) and weekend (marks its readers "re-run"). */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40" data-testid="eng-settings-locale">
            <Label className="text-xs font-semibold">{t("engLocale.section")}</Label>
            <div className="mt-2">
              <EngagementLocaleFields value={locale} onChange={setLocale} t={t} lang={lang} errors={localeErr} disabled={engLocked}
                                      testIdPrefix="eng-settings-locale" suggestOnCountry={false} />
            </div>
            {currencyToConfirm && !currencyDirty && !engLocked && (
              <label className="mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                <input type="checkbox" className="mt-0.5" checked={confirmCurrency} onChange={(e) => setConfirmCurrency(e.target.checked)}
                       data-testid="eng-settings-currency-confirm" />
                <span>{t("engLocale.confirmCurrency").replace("{cur}", currencyOfValue(savedLocale))}</span>
              </label>
            )}
            {weekendDiffers && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="eng-settings-locale-differs">
                {t("engLocale.countryWeekendDiffers").replace("{saved}", dayList(weekendNow)).replace("{country}", countryName(locale.country, lang))
                  .replace("{suggested}", dayList(suggested))}
              </div>
            )}
          </div>

          {/* Batch 2 · R65 — engagement period: start drives the report edition and the checklist default. */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40" data-testid="eng-settings-period">
            <Label className="text-xs font-semibold">{t("engSettings.period")}</Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">{t("engagements.periodStart")}</Label>
                <DateInput data-testid="eng-settings-period-start" value={periodStart} onChange={setPeriodStart} disabled={engLocked} />
              </div>
              <div>
                <Label className="text-[11px]">{t("engagements.periodEnd")}</Label>
                <DateInput data-testid="eng-settings-period-end" value={periodEnd} onChange={setPeriodEnd} disabled={engLocked} />
              </div>
            </div>
            <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{t("engSettings.periodHint")}</div>
          </div>

          {/* Round 24 · K1 — reporting framework drives which Disclosure Checklist is shown. */}
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20">
            <Label className="text-xs font-semibold">{t("engSettings.repFramework")}</Label>
            <div className="mt-2 flex gap-2" role="radiogroup" data-testid="eng-settings-rf-group">
              {[["full", t("engSettings.rfFull")], ["sme", t("engSettings.rfSme")]].map(([val, label]) => {
                const active = repFw === val;
                return (
                  <button key={val} type="button" role="radio" aria-checked={active} disabled={engLocked}
                          onClick={() => setRepFw(val)}
                          className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition ${active ? "border-amber-500 bg-white font-semibold text-amber-800 shadow-sm dark:bg-slate-900" : "border-amber-200 bg-transparent text-slate-600 hover:bg-white"} ${engLocked ? "cursor-not-allowed opacity-60" : ""}`}
                          data-testid={`eng-settings-rf-${val}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">{t("engSettings.rfDesc")}</div>
            {fwMismatch && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-100/60 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="eng-settings-fw-mismatch-rf">
                {t("engSettings.fwMismatch").replace("{pf}", pf).replace("{rf}", t("engSettings.rfSme"))}
              </div>
            )}
          </div>

          {/* Round 25c · R5 — report flags (ISA 700/705/706/710 inputs) · R7 — report format */}
          <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-900 dark:bg-sky-950/20" data-testid="eng-settings-report-flags">
            <Label className="text-xs font-semibold">{t("engSettings.reportFlags")}</Label>
            <div className="mt-2 grid grid-cols-2 gap-1 text-xs md:grid-cols-3">
              {["pte", "pie", "group", "comparative_fs", "predecessor_auditor", "prior_unaudited", "other_legal_regulatory", "compliance_framework", "true_and_fair"].map((k) => (
                <label key={k} className="flex items-center gap-1.5">
                  <input type="checkbox" disabled={engLocked} checked={!!flags[k]} onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })} data-testid={`eng-settings-flag-${k}`} />
                  {t(`engSettings.flag_${k}`)}
                </label>
              ))}
            </div>
            <input className="mt-2 w-full rounded border px-2 py-1 text-xs" disabled={engLocked} placeholder={t("engSettings.flag_addressee")} value={flags.addressee || ""}
                   onChange={(e) => setFlags({ ...flags, addressee: e.target.value })} data-testid="eng-settings-flag-addressee" />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground">{t("engSettings.flagsLogged")}</span>
            </div>
            <Label className="mt-3 block text-xs font-semibold">{t("engSettings.reportFormat")}</Label>
            {user?.role === "Partner" ? (
              <select className="mt-1 w-full rounded border px-2 py-1 text-xs" disabled={engLocked} value={rptFmt} onChange={(e) => setRptFmt(e.target.value)} data-testid="eng-settings-report-format">
                <option value="platform">{t("engSettings.formatPlatform")}</option>
                {formats.map((f) => <option key={f.id} value={f.id}>{f.name} · {f.jurisdiction} · V{f.version_no}</option>)}
              </select>
            ) : (
              <div className="mt-1 rounded border bg-slate-50 px-2 py-1 text-xs dark:bg-slate-900" data-testid="eng-settings-report-format-readonly">
                {engagement?.report_format?.kind === "firm" ? `${engagement.report_format.name} · V${engagement.report_format.format_version}` : t("engSettings.formatPlatform")}
                <span className="ms-2 text-[10px] text-muted-foreground">{t("engSettings.formatPartnerOnly")}</span>
              </div>
            )}
          </div>

          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/20">
            <Label className="text-xs font-semibold">
              {t("engSettings.presFramework")} <span className="text-red-600">*</span>
            </Label>
            <div className="mt-2 flex gap-2" role="radiogroup" data-testid="eng-settings-pf-group">
              {["IAS 1", "IFRS 18"].map((opt) => {
                const active = pf === opt;
                return (
                  <button key={opt} type="button" role="radio"
                          aria-checked={active}
                          disabled={engLocked}
                          onClick={() => setPf(opt)}
                          className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition ${
                            active
                              ? "border-emerald-500 bg-white font-semibold text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300"
                              : "border-emerald-200 bg-transparent text-slate-600 hover:bg-white dark:hover:bg-slate-900/60"
                          } ${engLocked ? "cursor-not-allowed opacity-60" : ""}`}
                          data-testid={`eng-settings-pf-${opt.replace(/\s+/g, "").toLowerCase()}`}>
                    {opt}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {t("engSettings.editableDesc")}
            </div>
            {fsGenerated && (
              <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-300" data-testid="eng-settings-pf-fs-generated">
                {t("engSettings.fsGeneratedNote")}
              </div>
            )}
            {fwMismatch && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-100/60 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="eng-settings-fw-mismatch-pf">
                {t("engSettings.fwMismatch").replace("{pf}", pf).replace("{rf}", t("engSettings.rfSme"))}
              </div>
            )}
          </div>

          {/* Batch C · Round C1 · Q3 — Initial / Continuing engagement.
              Anyone can edit (no Partner-only gate) since it does not
              hide or reveal any modules — only drives the W7 procedures. */}
          <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-950/20">
            <Label className="text-xs font-semibold">{t("eng.status.label")}</Label>
            <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup"
                  aria-label={t("eng.status.label")}
                  data-testid="eng-settings-status-group">
              {[
                { id: "", label: t("eng.status.notSet") },
                { id: "initial", label: t("eng.status.initial") },
                { id: "continuing", label: t("eng.status.continuing") },
              ].map((opt) => {
                const active = engStatus === opt.id;
                return (
                  <button key={opt.id || "unset"} type="button" role="radio"
                    aria-checked={active}
                    onClick={() => setEngStatus(opt.id)}
                    className={`rounded-md border px-3 py-1.5 text-xs transition ${active ? "border-blue-500 bg-white font-semibold text-blue-700 shadow-sm dark:bg-slate-900 dark:text-blue-300" : "border-blue-200 bg-transparent text-slate-600 hover:bg-white dark:hover:bg-slate-900/60"}`}
                    data-testid={`eng-settings-status-${opt.id || "unset"}`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">{t("eng.status.hint")}</div>
          </div>

          {/* PR 58 / 59 fixes · F2 — "First-year engagement - no comparatives" (own route; Partner / Manager only) */}
          <FirstYearToggle engagement={engagement} locked={engLocked} onChanged={() => onSaved?.()} />

          {/* Requires EQC review toggle (ISA 220 R33) — Partner/Admin only */}
          {canToggleEqc && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-amber-600"
                  checked={requiresEqc}
                  disabled={engLocked}
                  onChange={(e) => setRequiresEqc(e.target.checked)}
                  data-testid="eng-settings-requires-eqc"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <ShieldCheck className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                    {t("engSettings.requiresEqc")}
                    <span className="text-[10px] font-normal text-muted-foreground">
                      {t("engSettings.isaCitation")}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-amber-900 dark:text-amber-200">
                    {t("engSettings.eqcOnDesc")}
                    <div className="mt-1">
                      {t("engSettings.eqcOffBlocked")}
                    </div>
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>
        <DialogFooter className="sticky bottom-0 border-t bg-white px-6 py-3 dark:bg-slate-900"
                        data-testid="eng-settings-footer">
          <Button variant="outline" onClick={close} data-testid="eng-settings-close">{t("common.close")}</Button>
          <SaveCancelBar dirty={dirty} saving={saving} onSave={save} onCancel={restore} lang={lang}
                         disabled={engLocked || !pf} testid="eng-settings-save-bar" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
